import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { formatMoney } from '../lib/format.js'

// Print/export modal for the Schedule.
//
// Two views — toggle between them:
//   1. Weekly grid — staff × day matrix, good for posting in the
//      kitchen so everyone can see who's working when.
//   2. Payroll list — grouped by staff, totals hours + cost. Good
//      for a past report after shifts are done (status filter
//      restricts to scheduled + completed for cost accuracy).
//
// Print uses window.print() with @media print CSS in index.css.
// On mobile the manager can "Print to PDF" via the OS share sheet.
// No PDF library, no extra bundle weight.
export default function PrintScheduleModal({
  open,
  onClose,
  initialWeekStart,   // YYYY-MM-DD — defaults the range start
  restaurantName = '',
}) {
  const [view, setView]           = useState('weekly')   // 'weekly' | 'payroll'
  const [preset, setPreset]       = useState('this-week')
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd]     = useState('')
  const [data, setData]           = useState(null)
  const [loading, setLoading]     = useState(false)
  const [error, setError]         = useState(null)

  // Compute the active date range from preset or custom inputs.
  const range = useMemo(() => {
    const today = new Date()
    const todayStr = isoDate(today)
    const monday = mondayOf(today)
    switch (preset) {
      case 'this-week':
        return { start: monday, end: addDays(monday, 6) }
      case 'next-2':
        return { start: monday, end: addDays(monday, 13) }
      case 'next-4':
        return { start: monday, end: addDays(monday, 27) }
      case 'past-2':
        return { start: addDays(monday, -14), end: addDays(monday, -1) }
      case 'past-4':
        return { start: addDays(monday, -28), end: addDays(monday, -1) }
      case 'mtd':
        return { start: isoDate(new Date(today.getFullYear(), today.getMonth(), 1)), end: todayStr }
      case 'custom':
        return customStart && customEnd ? { start: customStart, end: customEnd } : null
      default:
        return { start: monday, end: addDays(monday, 6) }
    }
  }, [preset, customStart, customEnd])

  // Reset state when the modal opens. Set the default custom dates to
  // a sensible week so switching to Custom isn't immediately broken.
  useEffect(() => {
    if (!open) return
    setView('weekly')
    setPreset('this-week')
    setData(null)
    setError(null)
    const monday = mondayOf(new Date())
    setCustomStart(monday)
    setCustomEnd(addDays(monday, 6))
  }, [open, initialWeekStart])

  // Fetch whenever the active range changes.
  useEffect(() => {
    if (!open || !range) {
      setData(null)
      return
    }
    let cancelled = false
    async function fetchRange() {
      setLoading(true)
      setError(null)
      try {
        const [assignRes, staffRes] = await Promise.all([
          supabase.from('schedule_assignments')
            .select(`
              id, shift_date, start_time, end_time, role, status,
              staff:staff_id(id, name, role, hourly_wage)
            `)
            .gte('shift_date', range.start)
            .lte('shift_date', range.end)
            .order('shift_date')
            .order('start_time'),
          supabase.from('staff')
            .select('id, name, role, hourly_wage, active')
            .order('name'),
        ])
        if (assignRes.error) throw assignRes.error
        if (cancelled) return
        setData({
          assignments: assignRes.data ?? [],
          staff:       staffRes.data ?? [],
        })
      } catch (e) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error('[PrintScheduleModal] fetch failed', e)
          setError('Could not load shifts for that range.')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchRange()
    return () => { cancelled = true }
  }, [open, range?.start, range?.end])

  if (!open) return null

  function handlePrint() {
    // Defer to after the next paint so the @media print rules apply
    // to the most up-to-date DOM.
    setTimeout(() => window.print(), 50)
  }

  return (
    <div className="fixed inset-0 z-40 print-modal-root">
      {/* Scrim — hidden in print */}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in print:hidden"
      />

      <div
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[95vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl print:absolute print:inset-0 print:max-h-none print:rounded-none print:shadow-none"
        role="dialog"
        aria-modal="true"
        aria-label="Print schedule"
      >
        {/* Sticky control bar — hidden in print */}
        <div className="sticky top-0 z-10 bg-white pt-2 print:hidden">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xl font-medium">Print schedule</h2>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handlePrint}
                disabled={loading || !data || (data.assignments.length === 0)}
                className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                Print
              </button>
              <button
                type="button"
                onClick={onClose}
                className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
                aria-label="Cancel"
              >
                Close
              </button>
            </div>
          </div>

          {/* Range + view toggles */}
          <div className="space-y-2 px-4 pb-3">
            <div className="flex flex-wrap gap-1.5 text-xs">
              <PresetChip active={preset === 'this-week'} onClick={() => setPreset('this-week')}>This week</PresetChip>
              <PresetChip active={preset === 'next-2'}    onClick={() => setPreset('next-2')}>Next 2 weeks</PresetChip>
              <PresetChip active={preset === 'next-4'}    onClick={() => setPreset('next-4')}>Next 4 weeks</PresetChip>
              <PresetChip active={preset === 'past-2'}    onClick={() => setPreset('past-2')}>Past 2 weeks</PresetChip>
              <PresetChip active={preset === 'past-4'}    onClick={() => setPreset('past-4')}>Past 4 weeks</PresetChip>
              <PresetChip active={preset === 'mtd'}       onClick={() => setPreset('mtd')}>Month to date</PresetChip>
              <PresetChip active={preset === 'custom'}    onClick={() => setPreset('custom')}>Custom</PresetChip>
            </div>

            {preset === 'custom' && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="mb-0.5 block text-[11px] text-black/55" htmlFor="prn-start">Start</label>
                  <input
                    id="prn-start"
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm outline-none focus:border-blue-600"
                  />
                </div>
                <div>
                  <label className="mb-0.5 block text-[11px] text-black/55" htmlFor="prn-end">End</label>
                  <input
                    id="prn-end"
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm outline-none focus:border-blue-600"
                  />
                </div>
              </div>
            )}

            <div className="flex gap-1.5 text-xs">
              <ViewChip active={view === 'weekly'}  onClick={() => setView('weekly')}>Weekly grid (post in kitchen)</ViewChip>
              <ViewChip active={view === 'payroll'} onClick={() => setView('payroll')}>Payroll list (per employee)</ViewChip>
            </div>

            {error && (
              <div role="alert" className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white">
                {error}
              </div>
            )}
          </div>
        </div>

        {/* Printable content area */}
        <div className="px-4 pb-6 print:px-6 print:pb-0">
          {!range ? (
            <p className="py-6 text-center text-sm text-black/55">
              Pick a date range to preview.
            </p>
          ) : loading ? (
            <p className="py-6 text-center text-sm text-black/55">Loading…</p>
          ) : data && data.assignments.length === 0 ? (
            <p className="py-6 text-center text-sm text-black/55">
              No shifts in this range.
            </p>
          ) : data ? (
            view === 'weekly' ? (
              <WeeklyGridPrintView
                assignments={data.assignments}
                staff={data.staff}
                rangeStart={range.start}
                rangeEnd={range.end}
                restaurantName={restaurantName}
              />
            ) : (
              <PayrollListPrintView
                assignments={data.assignments}
                staff={data.staff}
                rangeStart={range.start}
                rangeEnd={range.end}
                restaurantName={restaurantName}
              />
            )
          ) : null}
        </div>
      </div>
    </div>
  )
}

// ---------- Sub-components --------------------------------

function PresetChip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-1 font-medium transition ${
        active
          ? 'border-blue-600 bg-blue-600 text-white'
          : 'border-black/15 bg-white text-black/70 hover:border-blue-600 hover:text-blue-700'
      }`}
    >
      {children}
    </button>
  )
}

function ViewChip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 rounded-lg border px-3 py-2 font-medium transition ${
        active
          ? 'border-blue-600 bg-blue-50 text-blue-800'
          : 'border-black/15 bg-white text-black/70 hover:border-blue-600'
      }`}
    >
      {children}
    </button>
  )
}

// Weekly grid — rows = staff who have at least one shift in the
// range, columns = days. Designed to fit landscape letter when the
// range is exactly 7 days; longer ranges break into multiple weekly
// sections so each prints clearly.
function WeeklyGridPrintView({ assignments, staff, rangeStart, rangeEnd, restaurantName }) {
  // Split into 7-day chunks so multi-week ranges paginate sensibly
  // (one full week per page when printed).
  const weeks = useMemo(() => {
    const out = []
    let cursor = mondayOnOrBefore(rangeStart)
    while (cursor <= rangeEnd) {
      const weekEnd = addDays(cursor, 6)
      out.push({ start: cursor, end: weekEnd > rangeEnd ? rangeEnd : weekEnd })
      cursor = addDays(cursor, 7)
    }
    return out
  }, [rangeStart, rangeEnd])

  return (
    <div className="print-area">
      <h1 className="text-xl font-medium print:text-2xl">
        Schedule {restaurantName ? `\u00b7 ${restaurantName}` : ''}
      </h1>
      <div className="text-sm text-black/60 print:text-base">
        {prettyRange(rangeStart, rangeEnd)}
      </div>

      {weeks.map((wk) => (
        <WeeklyGridSection
          key={wk.start}
          start={wk.start}
          end={wk.end}
          assignments={assignments}
          staff={staff}
        />
      ))}
    </div>
  )
}

function WeeklyGridSection({ start, end, assignments, staff }) {
  const days = useMemo(() => {
    const arr = []
    let cur = start
    while (cur <= end) {
      arr.push(cur)
      cur = addDays(cur, 1)
    }
    return arr
  }, [start, end])

  // Filter to assignments inside this week + only staff that have
  // any shifts in it (so the printed grid stays compact).
  const weekAssign = assignments.filter(
    (a) => a.shift_date >= start && a.shift_date <= end
  )
  const staffIdsInWeek = new Set(weekAssign.map((a) => a.staff?.id).filter(Boolean))
  const visibleStaff = staff.filter((s) => staffIdsInWeek.has(s.id))

  // Lookup: staff_id|day → assignments[]
  const cellMap = new Map()
  for (const a of weekAssign) {
    const key = `${a.staff?.id}|${a.shift_date}`
    if (!cellMap.has(key)) cellMap.set(key, [])
    cellMap.get(key).push(a)
  }

  // Per-staff weekly hours total
  const totals = new Map()
  for (const s of visibleStaff) {
    let h = 0
    for (const a of weekAssign) {
      if (a.staff?.id !== s.id) continue
      if (a.status === 'scheduled' || a.status === 'completed') {
        h += hoursBetween(a.start_time, a.end_time)
      }
    }
    totals.set(s.id, h)
  }

  return (
    <div className="mt-4 break-inside-avoid print:mt-6">
      <div className="mb-1 text-sm font-medium print:text-base">
        Week of {prettyDate(start)}
      </div>
      <table className="w-full border-collapse text-xs print:text-[11px]">
        <thead>
          <tr>
            <th className="border border-black/30 bg-black/5 px-2 py-1 text-left">Staff</th>
            {days.map((d) => (
              <th key={d} className="border border-black/30 bg-black/5 px-1 py-1 text-left">
                <div>{shortWeekday(d)}</div>
                <div className="text-[10px] font-normal opacity-70">{shortDate(d)}</div>
              </th>
            ))}
            <th className="border border-black/30 bg-black/5 px-2 py-1 text-right">Hours</th>
          </tr>
        </thead>
        <tbody>
          {visibleStaff.length === 0 ? (
            <tr>
              <td colSpan={days.length + 2} className="border border-black/20 px-2 py-2 text-center text-black/50">
                No shifts this week
              </td>
            </tr>
          ) : (
            visibleStaff.map((s) => (
              <tr key={s.id}>
                <td className="border border-black/20 px-2 py-1 align-top font-medium">
                  {s.name}
                  {s.role && <div className="text-[10px] font-normal opacity-70">{s.role}</div>}
                </td>
                {days.map((d) => {
                  const cell = cellMap.get(`${s.id}|${d}`) ?? []
                  return (
                    <td key={d} className="border border-black/20 px-1 py-1 align-top">
                      {cell.length === 0 ? (
                        <span className="text-black/30">{'\u2014'}</span>
                      ) : (
                        cell.map((a) => (
                          <div key={a.id} className={a.status === 'called_out' || a.status === 'no_show' ? 'line-through opacity-60' : ''}>
                            {compactTime(a.start_time)}{'\u2013'}{compactTime(a.end_time)}
                            {a.role ? <div className="text-[10px] opacity-70">{a.role}</div> : null}
                          </div>
                        ))
                      )}
                    </td>
                  )
                })}
                <td className="border border-black/20 px-2 py-1 text-right tabular-nums">
                  {totals.get(s.id).toFixed(1)}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}

// Payroll list — grouped by employee, lists each shift, totals at the bottom.
// Counts only scheduled/completed shifts in cost calc.
function PayrollListPrintView({ assignments, staff, rangeStart, rangeEnd, restaurantName }) {
  const byStaff = useMemo(() => {
    const m = new Map()
    for (const a of assignments) {
      const sid = a.staff?.id
      if (!sid) continue
      if (!m.has(sid)) m.set(sid, { staff: a.staff, shifts: [], hours: 0, cost: 0 })
      const e = m.get(sid)
      e.shifts.push(a)
      if (a.status === 'scheduled' || a.status === 'completed') {
        const h = hoursBetween(a.start_time, a.end_time)
        const wage = Number(a.staff?.hourly_wage || 0)
        e.hours += h
        e.cost  += h * wage
      }
    }
    return [...m.values()].sort((a, b) => a.staff.name.localeCompare(b.staff.name))
  }, [assignments])

  const grandHours = byStaff.reduce((s, e) => s + e.hours, 0)
  const grandCost  = byStaff.reduce((s, e) => s + e.cost, 0)

  return (
    <div className="print-area">
      <h1 className="text-xl font-medium print:text-2xl">
        Payroll Report {restaurantName ? `\u00b7 ${restaurantName}` : ''}
      </h1>
      <div className="text-sm text-black/60 print:text-base">
        {prettyRange(rangeStart, rangeEnd)}
      </div>
      <div className="mt-2 rounded border border-black/20 px-3 py-2 text-sm">
        Grand total:{' '}
        <span className="font-medium tabular-nums">{grandHours.toFixed(1)} hrs</span>
        {' \u00b7 '}
        <span className="font-medium tabular-nums">{formatMoney(grandCost)}</span>
      </div>

      {byStaff.map((e) => (
        <div key={e.staff.id} className="mt-4 break-inside-avoid">
          <div className="border-b border-black/30 pb-1 text-sm font-medium print:text-base">
            {e.staff.name}
            {e.staff.role ? ` \u00b7 ${e.staff.role}` : ''}
            {e.staff.hourly_wage != null ? ` \u00b7 ${formatMoney(e.staff.hourly_wage)}/hr` : ''}
          </div>
          <table className="mt-1 w-full border-collapse text-xs print:text-[11px]">
            <thead>
              <tr className="text-left">
                <th className="px-2 py-1">Date</th>
                <th className="px-2 py-1">Day</th>
                <th className="px-2 py-1">Shift</th>
                <th className="px-2 py-1">Role</th>
                <th className="px-2 py-1 text-right">Hours</th>
                <th className="px-2 py-1 text-right">Cost</th>
                <th className="px-2 py-1">Status</th>
              </tr>
            </thead>
            <tbody>
              {e.shifts.map((a) => {
                const counts = a.status === 'scheduled' || a.status === 'completed'
                const hrs = hoursBetween(a.start_time, a.end_time)
                const cost = counts ? hrs * Number(a.staff?.hourly_wage || 0) : 0
                return (
                  <tr key={a.id} className="border-t border-black/15">
                    <td className="px-2 py-1 tabular-nums">{a.shift_date}</td>
                    <td className="px-2 py-1">{shortWeekday(a.shift_date)}</td>
                    <td className="px-2 py-1 tabular-nums">
                      {compactTime(a.start_time)}{'\u2013'}{compactTime(a.end_time)}
                    </td>
                    <td className="px-2 py-1">{a.role || e.staff.role || ''}</td>
                    <td className="px-2 py-1 text-right tabular-nums">
                      {hrs.toFixed(1)}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums">
                      {counts ? formatMoney(cost) : '—'}
                    </td>
                    <td className="px-2 py-1 text-[10px]">
                      {a.status === 'scheduled' ? '' : a.status.replace('_', ' ')}
                    </td>
                  </tr>
                )
              })}
              <tr className="border-t border-black/40 font-medium">
                <td colSpan={4} className="px-2 py-1 text-right">Subtotal:</td>
                <td className="px-2 py-1 text-right tabular-nums">{e.hours.toFixed(1)}</td>
                <td className="px-2 py-1 text-right tabular-nums">{formatMoney(e.cost)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      ))}
    </div>
  )
}

// ---------- Date helpers (local to this file) ---------------
function isoDate(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}
function parseDate(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(y, m - 1, d, 0, 0, 0, 0)
}
function addDays(dateStr, n) {
  const d = parseDate(dateStr)
  d.setDate(d.getDate() + n)
  return isoDate(d)
}
function mondayOf(date) {
  const d = new Date(date)
  const day = d.getDay()
  const diff = day === 0 ? -6 : 1 - day
  d.setDate(d.getDate() + diff)
  return isoDate(d)
}
function mondayOnOrBefore(dateStr) {
  return mondayOf(parseDate(dateStr))
}
function shortWeekday(dateStr) {
  return parseDate(dateStr).toLocaleDateString('en-US', { weekday: 'short' })
}
function shortDate(dateStr) {
  return parseDate(dateStr).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })
}
function prettyDate(dateStr) {
  return parseDate(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  })
}
function prettyRange(start, end) {
  return `${prettyDate(start)} \u2013 ${prettyDate(end)}`
}
function compactTime(t) {
  // "17:00:00" → "5p" / "17:30" → "5:30p"
  if (!t) return ''
  const [hh, mm] = t.split(':').map(Number)
  const half = hh >= 12 ? 'p' : 'a'
  let h12 = hh % 12
  if (h12 === 0) h12 = 12
  return mm === 0 ? `${h12}${half}` : `${h12}:${String(mm).padStart(2, '0')}${half}`
}
function hoursBetween(start, end) {
  if (!start || !end) return 0
  const [sh, sm] = start.split(':').map(Number)
  const [eh, em] = end.split(':').map(Number)
  const startMin = sh * 60 + sm
  let endMin     = eh * 60 + em
  if (endMin < startMin) endMin += 24 * 60
  return Math.max(0, (endMin - startMin) / 60)
}
